import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  closestCorners,
} from '@dnd-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Phone, Calendar, Wrench, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { enquiriesAPI } from '@/utils/api';

// Workflow columns — ordered pipeline. `in_progress` and `cancelled` are kept in the
// underlying data model but intentionally not shown as columns per product spec.
const COLUMNS = [
  { id: 'new', label: 'New', accent: 'border-t-blue-500', badge: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  { id: 'contacted', label: 'Contacted', accent: 'border-t-indigo-500', badge: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30' },
  { id: 'quoted', label: 'Quoted', accent: 'border-t-amber-500', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { id: 'booked', label: 'Booked', accent: 'border-t-emerald-500', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  { id: 'completed', label: 'Completed', accent: 'border-t-gray-500', badge: 'bg-gray-500/10 text-gray-300 border-gray-500/30' },
];

const SERVICE_LABEL = {
  handyman: 'Handyman',
  air_conditioning: 'Air Conditioning',
  refrigeration: 'Refrigeration',
  plumbing: 'Plumbing',
  multiple: 'Multiple',
  other: 'Other',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const EnquiryCard = ({ enquiry, isOverlay = false }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: enquiry.enquiry_id,
    data: { status: enquiry.status, enquiry },
    disabled: isOverlay,
  });

  const isEmergency = enquiry.urgency === 'emergency';
  const serviceText = SERVICE_LABEL[enquiry.service_type] || enquiry.service_type;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`kanban-card-${enquiry.enquiry_id}`}
      className={`group select-none cursor-grab active:cursor-grabbing rounded-md border bg-gray-900 p-3 transition-all duration-150
        ${isEmergency ? 'border-red-500/50 hover:border-red-400' : 'border-gray-800 hover:border-amber-500/60'}
        ${isDragging && !isOverlay ? 'opacity-0' : 'opacity-100'}
        ${isOverlay ? 'shadow-2xl shadow-amber-500/20 rotate-1' : 'hover:shadow-lg hover:shadow-amber-500/10'}
      `}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-white font-semibold text-sm leading-tight truncate">{enquiry.customer_name}</p>
        {isEmergency && (
          <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-red-500/10 text-red-400 border-red-500/30 uppercase tracking-wide">
            <AlertTriangle className="w-3 h-3" /> SOS
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-1.5">
        <Wrench className="w-3 h-3 text-amber-500" />
        <span className="truncate">{serviceText}</span>
      </div>

      {enquiry.customer_phone && (
        <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1.5">
          <Phone className="w-3 h-3" />
          <span className="truncate">{enquiry.customer_phone}</span>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-800/70">
        <div className="flex items-center gap-1 text-[11px] text-gray-500">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(enquiry.created_at)}</span>
        </div>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border capitalize ${COLUMNS.find((c) => c.id === enquiry.status)?.badge || 'bg-gray-800 text-gray-300 border-gray-700'}`}>
          {enquiry.status.replace(/_/g, ' ')}
        </span>
      </div>
    </div>
  );
};

const Column = ({ column, enquiries }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${column.id}`}
      className={`flex flex-col rounded-lg border border-t-2 ${column.accent} bg-gray-900/40 border-gray-800 transition-colors
        ${isOver ? 'ring-2 ring-amber-500/60 bg-gray-900/70' : ''}
      `}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-800">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300">{column.label}</h3>
        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${column.badge}`}>
          {enquiries.length}
        </span>
      </div>

      <div className="flex flex-col gap-2 p-2 min-h-[120px] max-h-[520px] overflow-y-auto">
        {enquiries.length === 0 ? (
          <div className="flex items-center justify-center text-[11px] text-gray-600 italic py-6 border border-dashed border-gray-800 rounded-md">
            Drop here
          </div>
        ) : (
          enquiries.map((e) => <EnquiryCard key={e.enquiry_id} enquiry={e} />)
        )}
      </div>
    </div>
  );
};

const LeadFunnelKanban = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const loadEnquiries = async () => {
    try {
      setLoading(true);
      // Backend caps `limit` at 100 per page. Fetch up to that; if the tenant
      // grows past 100 open enquiries we can iterate with skip/limit later.
      const data = await enquiriesAPI.getAll({ limit: 100 });
      setEnquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEnquiries();
  }, []);

  // Group by column. Anything not in the 5 columns (e.g. in_progress, cancelled)
  // is intentionally excluded from the board.
  const grouped = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    for (const e of enquiries) {
      if (map[e.status]) map[e.status].push(e);
    }
    // Emergencies first, then newest first
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        if (a.urgency === 'emergency' && b.urgency !== 'emergency') return -1;
        if (b.urgency === 'emergency' && a.urgency !== 'emergency') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
    return map;
  }, [enquiries]);

  const activeEnquiry = useMemo(
    () => enquiries.find((e) => e.enquiry_id === activeId) || null,
    [activeId, enquiries]
  );

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const newStatus = over.id;
    const moved = enquiries.find((e) => e.enquiry_id === active.id);
    if (!moved || moved.status === newStatus) return;
    if (!COLUMNS.some((c) => c.id === newStatus)) return;

    // Optimistic update
    const previous = enquiries;
    setEnquiries((prev) =>
      prev.map((e) => (e.enquiry_id === active.id ? { ...e, status: newStatus } : e))
    );

    try {
      await enquiriesAPI.updateStatus(active.id, newStatus);
      toast.success(`Moved ${moved.customer_name} \u2192 ${COLUMNS.find((c) => c.id === newStatus).label}`);
    } catch (err) {
      console.error(err);
      setEnquiries(previous); // rollback
      toast.error(err.message || 'Failed to update status');
    }
  };

  return (
    <Card className="bg-gray-900/50 border-gray-800">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-white">Lead Funnel</CardTitle>
          <p className="text-xs text-gray-500 mt-1">Drag enquiries between stages. Changes save automatically.</p>
        </div>
        <button
          onClick={loadEnquiries}
          data-testid="kanban-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-500 transition-colors border border-gray-800 hover:border-amber-500/50 rounded px-2.5 py-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </CardHeader>
      <CardContent>
        {loading && enquiries.length === 0 ? (
          <div className="text-gray-500 text-sm py-10 text-center">Loading pipeline...</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={(e) => setActiveId(e.active.id)}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
          >
            <div
              data-testid="kanban-board"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
            >
              {COLUMNS.map((col) => (
                <Column key={col.id} column={col} enquiries={grouped[col.id]} />
              ))}
            </div>
            <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
              {activeEnquiry ? <EnquiryCard enquiry={activeEnquiry} isOverlay /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadFunnelKanban;
